import React, {useEffect, useState} from "react";
import {Box, Divider} from '@mui/material';

import "../App.css"
import {PageHeaderItems} from "./PageHeader";
import {DocPanel} from "./DocPanel";
import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {CxGrid} from "../cmx/CxGrid";
import {
    CxTextField,
    CxDatePicker,
    CxDatePickerNative,
    CxCheckBox,
    CxCancelSaveButton,
    CxDeleteCancelSaveButton,
    CxRadioboxSelection,
    CxComboxSelection,
    CxAutocomplete, CxMetaInfo, CxLocalization, CxCloseButton
} from "../cmx/CxForm";
import {CxIconButtonSecondary} from "../cmx/CxButton";
import {CxConfirmDialog} from "../cmx/CxDialog";
import {CxFormFeedback, CxSnack} from "../cmx/CxFeedback";
import {CxPopover} from "../cmx/CxPopover";
import {canCollabDoc, canManageDoc} from "../ctx/AccessCtrl";
import {Team} from "../domain/Team";
import {Task} from "../domain/Task";
import {initialCall, runningCall} from "../gateway/CallService";
import {taskSave, taskDelete, taskFavoritesList} from "../gateway/TaskService";
import {DateUtil} from "../lib/DateUtil"
import {ValidationUtil} from "../lib/ValidationUtil";
import {JsonUtil} from '../lib/JsonUtil';

// --------------------------------------------------------------------------------------------------------------------
// RenderTask
// --------------------------------------------------------------------------------------------------------------------

const RenderTask = (title, currentFormValues, isUpdate, readOnly=false) => {
    const MAX_EXTENSIONS = 6;

    const [formValues, setFormValues] = useState(currentFormValues);
    const [formErrors, setFormErrors] = useState(Task.getFormErrors());
    const [openConfirm, setOpenConfirm] = useState(false);
    const [call, setCall] = useState(initialCall())

    const {handleAction, currentReturnTo} = useAppCtx();
    const {code, lazyloadCodes} = useCodeCtx();
    const {crew, lazyloadCrews } = useCrewCtx();
    const [snack, setSnack] = useState(null);

    const [favorites, setFavorites] = useState([]);
    const [favoritesLoading, setFavoritesLoading] = useState(false);

    useEffect(() => {
        lazyloadCodes();
        lazyloadCrews();
    }, []);

    //
    // handers
    //
    const handleChange = (fieldOrEvent, value) => {

        // DatePicker
        if (typeof fieldOrEvent === "string") {

            if (fieldOrEvent === "start_date" && !formValues['end_date']) {
                setFormValues((prev) => ({
                    ...prev,
                    start_date: DateUtil.convertDateToIsoString(value),
                    end_date: DateUtil.convertDateToIsoString(value)
                }));
                setFormErrors((prev) => ({
                    ...prev,
                    start_date: false,
                    end_date: false
                }));

            } else {
                setFormValues((prev) => ({ ...prev, [fieldOrEvent]: DateUtil.convertDateToIsoString(value) }));
                setFormErrors((prev) => ({ ...prev, [fieldOrEvent]: false }));
            }

        // Others
        } else {
            const { name, value, type, checked} = fieldOrEvent.target;

            // CheckBox: Convert checked state to 0/1
            if (type === "checkbox") {
                setFormValues((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));

            // TextField: Sanitize and update value
            } else {
                setFormValues((prev) => ({ ...prev, [name]: value }));
            }
            setFormErrors((prev) => ({ ...prev, [name]: false }));
        }
    };

    const handleComboxChange = (field, value) => {

        // set task_code_key and dependend fields
        if (field === "task_code_key") {
            setFormValues((prevValues) => ({
                ...prevValues,
                task_code_key: value,
                sub_task_code_key: -1,  // reset sub task selection
                warranty_crew_id: -1,   // reset warranty crew id
                warranty_cause: ""      // reset warranty cause
            }));


        // set value of other combox fields
        } else {
            setFormValues((prevValues) => ({
                ...prevValues,
                [field]: value
            }));
        }

        // reset error
        setFormErrors((prev) => ({ ...prev, [field]: false }));
    };

    const handleSubTaskChange = (event, item) => {
        let value = -1;     // reset selection if item is null
        if (item) {
            value = item.value;
        }

        if ( isWarranty() ) {
            setFormValues((prevValues) => ({
                ...prevValues,
                sub_task_code_key: value
            }));
        } else {
            setFormValues((prevValues) => ({
                ...prevValues,
                sub_task_code_key: value,
                warranty_cause: ""      // reset warranty cause
            }));
        }

        // pump handling
        if ( willBePump(value) ) {
            setFormValues((prevValues) => ({
                ...prevValues,
                pump_code_key: "tool.0.0"       // PRESET to Elektropumpe
            }));
        } else {
            setFormValues((prevValues) => ({
                ...prevValues,
                pump_code_key: -1               // RESET to keine Pumpe
            }));
        }
    }

    const handleTeamLeadSourceChange = (value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            team_lead_source: value,
            team_lead_crew_id: null,
            team_member_crew_ids: []
        }));
        formErrors.team_lead_crew_id = "";
    }

    const handleTeamLeadChange = (event, item) => {
        // set value or reset if item is null
        let teamLeadCrewId = null;
        let teamMemberIds = [];
        if (item) {
            teamLeadCrewId = item.value;
            // eslint-disable-next-line eqeqeq
            if (formValues['team_lead_source'] == 0 ) { 
                teamMemberIds = crew.getTeamMemberIds(teamLeadCrewId);
            }
        }
        setFormValues((prevValues) => ({
            ...prevValues,
            team_lead_crew_id: teamLeadCrewId,
            team_member_crew_ids: teamMemberIds
        }));

        // reset error
        setFormErrors((prev) => ({ ...prev, team_lead_crew_id: false }));
    };

    const handleTeamMemberChange = (event, item) => {
        // item has objects like [{id: ..., label:...}, {value: ..., label:...}]
        let memberCrewIds = item.map(i => i.id ?? i.value);
        memberCrewIds = Team.filterUniqueMembers(memberCrewIds);

        // check if teamLeadCrewId is part of the members and remove the member entry
        const leadId = formValues['team_lead_crew_id'];
        const isLeadInMembers = Team.isLeadInMembers(memberCrewIds,leadId);
        const memberIds = isLeadInMembers ? Team.filterMembers(memberCrewIds, leadId) : memberCrewIds;
        if (isLeadInMembers) {
            const name = crew.getName(leadId);
            const snack= `${name} ist bereits als Gruppenführer/Mitarbeiter erfasst.`;
            setSnack(snack)
        }

        // update form values
        setFormValues((prevValues) => ({
            ...prevValues,
            team_member_crew_ids: memberIds
        }));
        setFormErrors((prev) => ({ ...prev, team_member_crew_ids: false }));
    }

    const handleWarrantyChange = (event, item) => {
        // set value or reset if item is null
        let warrantyCrewId = null;
        if (item) {
            warrantyCrewId = item.value;
        }
        setFormValues((prevValues) => ({
            ...prevValues,
            warranty_crew_id: warrantyCrewId
        }));

        // reset error
        setFormErrors((prev) => ({ ...prev, warranty_crew_id: false }));
    };

    const handleCloseSnack = () => {
        setSnack(null);
    };

    // Lazy loader triggered by CxPopover
    const handleFavoriteLoad = async () => {
        // optimization: Don't fetch if already loading or if we already have data
        if (favoritesLoading || favorites.length > 0) return;

        // load data
        try {
            setFavoritesLoading(true);

            const response = await taskFavoritesList(formValues['project_id']);
            let mappedRows = [];
            if (response.isSuccess) {
                mappedRows = Task.mapListColumns(response.result, code, crew);
            } else {
                console.error("Error loading favorites:", response.result);
            }
            setFavorites(mappedRows);

        } catch (error) {
            console.error("Error loading favorites:", error);
        } finally {
            setFavoritesLoading(false);
        }
    };

    const handleFavoriteSelect = (row) => {
        const teamLeadSource = row['team_lead_source'];
        const teamLeadCrewId = row['team_lead_crew_id'];
        const teamMemberIds = teamLeadSource === 0 ? crew.getTeamMemberIds(teamLeadCrewId) : [];

        // set new values
        setFormValues((prevValues) => ({
            ...prevValues,
            team_lead_source: teamLeadSource,
            team_lead_crew_id: teamLeadCrewId,
            team_member_crew_ids: teamMemberIds
        }));

        // reset error
        setFormErrors((prev) => ({
            ...prev,
            team_lead_source: false,
            team_lead_crew_id: false,
            team_member_crew_ids: false
        }));
    };

    const handleExtensionAdd = () => {
        // calculate initial date
        let baseDateStr = formValues.start_date || DateUtil.getCurrentDateAsIsoString();

        const extensions = formValues.extensions || [];
        if (extensions.length > 0) {
            baseDateStr = extensions[extensions.length - 1].date;
        }
        const baseDate = DateUtil.convertIsoStringToDate(baseDateStr);
        const nextDate = new Date(baseDate);
        nextDate.setDate(nextDate.getDate() + 1);
        const nextDateIso = DateUtil.convertDateToIsoString(nextDate);

        // create and add new extension
        const newExtension = Task.createExtension(nextDateIso, formValues['team_lead_crew_id'], formValues['team_member_crew_ids']);

        setFormValues(prev => ({
            ...prev,
            extensions: [...extensions, newExtension]
        }));
    };

    const handleExtensionRemove = (id) => {
        setFormValues(prev => ({
            ...prev,
            extensions: prev.extensions.filter(ext => ext.id !== id)
        }));
    };

    const handleExtensionChange = (id, field, value) => {
        let newValue = value;
        const fieldError = field + "_error";

        if (field === "date") {
            newValue = DateUtil.convertDateToIsoString(value);

        } else if (field === "crew_ids") {
            // get list of ids
            const memberCrewIds = value.map(i => i.id ?? i.value);
            // filter unique members and assign new value
            newValue = Team.filterUniqueMembers(memberCrewIds);
        }

        setFormValues(prev => ({
            ...prev,
            extensions: prev.extensions.map(ext =>
                ext.id === id ? { ...ext, [field]: newValue, [fieldError]: null } : ext
            )
        }));
    };

    const handleSubmit = async (action) => {
        if (action === "cancel" || action === "close") {
            if (isUpdate && currentReturnTo) {
                handleAction(currentReturnTo)
            } else if (currentReturnTo) {
                handleAction(currentReturnTo)
            } else {
                handleAction('projectDetail');
            }
        }

        if (action === "delete") {
            setOpenConfirm(true)
            return
        }

        if (action === "save") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);
            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save (create new or update existing entity)
            setCall(runningCall);
            const responseCall = await taskSave(Task.mapFormToDb(formValues))
            if (responseCall.isError) {
                setCall(responseCall)
                return
            }
            // return to
            const returnTo = currentReturnTo ? currentReturnTo : 'projectDetail';
            handleAction(returnTo);
        }
    };

    const handleConfirm = async (action) => {
        setOpenConfirm(false);
        if (action === "yes") {
            // delete task
            setCall(runningCall);
            const responseCall = await taskDelete(currentFormValues.id);
            if (responseCall.isError) {
                setCall(responseCall)
            } else {
                // return to
                const returnTo = currentReturnTo ? currentReturnTo : 'projectDetail';
                handleAction(returnTo);
            }
        }
    }


    //
    // check sub_task_code warranty
    //
    function isWarranty() {
        const sub_task_code_key = formValues["sub_task_code_key"]
        if ( sub_task_code_key === -1) {
            return false;
        }

        const sub_task_code = code.getCode(sub_task_code_key)[0];   // return list with one entry, pick firt from list
        if (sub_task_code.warranty !== 1) {
            return false;
        }
        return true;
    }

    //
    // check sub_task_code pump
    //
    function isPump() {
        const sub_task_code_key = formValues["sub_task_code_key"]
        if ( sub_task_code_key === -1) {
            return false;
        }

        const sub_task_code = code.getCode(sub_task_code_key)[0];   // return list with one entry, pick firt from list
        if (sub_task_code.pump !== 1) {
            return false;
        }
        return true;
    }

    function willBePump(sub_task_code_key) {
        if ( sub_task_code_key === -1) {
            return false;
        }

        const sub_task_code = code.getCode(sub_task_code_key)[0];   // return list with one entry, pick firt from list
        if (sub_task_code.pump !== 1) {
            return false;
        }
        return true;
    }


    //
    // validation
    //
    function validate(formValues, formErrors) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues)

        // validate required fields
        let requiredFields = Task.getFormRequiredFields();

        if (isWarranty()) {
            requiredFields.push("warranty_crew_id");
        }

        isValid = ValidationUtil.required(formValues, formErrors, requiredFields)

        // ensure end_date is equal or greater start_date
        if (! ValidationUtil.dateRange(formValues, formErrors, 'start_date', 'end_date') ){
            isValid = false;
        }

        // ensure team lead if team member's are available
        if (formValues.team_member_crew_ids &&
            formValues.team_member_crew_ids.length !==0 &&
            !formValues.team_lead_crew_id) {
            formErrors.team_lead_crew_id = "Auswahl erforderlich, wenn bereits Gruppenmitglieder zugeteilt sind.";
            isValid = false;
        } else {
            formErrors.team_lead_crew_id = "";
        }

        // validate extensions
        if (formValues.extensions && formValues.extensions.length > 0) {
            let seenDates = [];

            const updatedExtensions = formValues.extensions.map((ext, index) => {
                let dateError = null;
                let crewIdsError = null;

                // date checks
                if (!ext.date) {
                    dateError = "Eingabe erforderlich.";
                }
                else if (formValues.start_date && ext.date < formValues.start_date) {
                    dateError = "Datum muss gleich oder grösser Startdatum sein.";
                } else if (formValues.end_date && ext.date > formValues.end_date) {
                    dateError = "Datum muss kleiner oder gleich Enddatum sein.";
                } else if ( seenDates.includes(ext.date) ) {
                    dateError = "Datum bereits erfasst (Duplikat).";
                } else {
                    seenDates.push(ext.date);
                }

                // crew ids checks
                const crewIds = JsonUtil.convertJsonToObject(formValues.team_member_crew_ids)
                const teamCrewIds = [formValues.team_lead_crew_id, ...crewIds];

                if (!ext.crew_ids || ext.crew_ids.length === 0) {
                    crewIdsError = "Eingabe erforderlich.";
                } else if (ValidationUtil.containEqualIds(teamCrewIds, ext.crew_ids)) {
                    crewIdsError = "Identisch mit Haupt-Team (es ist keine Ausnahme notwendig).";
                }

                // check for errors
                if (dateError || crewIdsError) {
                    isValid = false;
                }

                // return object with updated values
                return {
                    ...ext,
                    date_error: dateError,
                    crew_ids_error: crewIdsError
                };
            });

            // important: update state
            setFormValues(prev => ({ ...prev, extensions: updatedExtensions }));
        }

        // return the final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formTextField = (field, label, maxLength, readOnly, numericOnly=false) => {
        return <CxTextField field={field} label={label} maxLength={maxLength} readOnly={readOnly} numericOnly={numericOnly}
                            handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formDatePicker = (field, label, readOnly) => {
        return <CxDatePicker field={field} label={label} readOnly={readOnly} disablePast={false}
                             handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCheckBox = (field, label, readOnly) => {
        return <CxCheckBox field={field} label={label} readOnly={readOnly}
                           handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items, readOnly) => {
        return <CxComboxSelection id={field} name={label} value={formValues[field]}
                                  onChange={(value) => handleComboxChange(field, value)}
                                  items={items} readOnly={readOnly} formErrors={formErrors} />
    }

    const formSubTaskSelection = (readOnly) => {
        const field =  "sub_task_code_key";
        const label = "Arbeitsvorgang *"
        const items = code.getSubTaskSelection(formValues["task_code_key"], null, true);
        let current_id = formValues[field]
        let currentValue = items.find(item => item.value === current_id) || null;

        return <CxAutocomplete key={field}
                               label={label}
                               currentValue={currentValue}
                               items={items}
                               readOnly={readOnly}
                               onChange={handleSubTaskChange}
                               error={formErrors[field]} />
    }

    const formTeamLeadSource = (readOnly) => {
        return <CxRadioboxSelection name='team_lead_source'
                                    label="Einsatz"
                                    value={formValues['team_lead_source']}
                                    items={crew.getTeamLeadSourceItems()}
                                    readOnly={readOnly}
                                    onChange={handleTeamLeadSourceChange}  />
    }

    const formTeamLeadSelection = (readOnly) => {
        let source = formValues['team_lead_source'];
        let items = crew.getTeamLeadItems(source);
        let current_id = formValues['team_lead_crew_id']
        let currentValue = items.find(item => item.value === current_id) || null;

        return <CxAutocomplete key="team_lead_selection"
                               label={crew.getTeamLeadSourceLabel(source)}
                               currentValue={currentValue}
                               items={items}
                               readOnly={readOnly}
                               onChange={handleTeamLeadChange}
                               error={formErrors['team_lead_crew_id']} />
    }

    const formTeamMemberSelection = (readOnly) => {
        let items = crew.getTeamLeadWorkerSelection();
        let currentValues = crew.getTeamMemberItems(formValues['team_member_crew_ids']);

        return <CxAutocomplete key="team_member_selection" label="Gruppemitglieder"
                               currentValue={currentValues}
                               items={items}
                               onChange={handleTeamMemberChange}
                               error={formErrors['team_member_crew_ids']}
                               readOnly={readOnly}
                               multiple={true} />
    }

    const formWarrantySelection = (readOnly) => {
        let items = crew.getWarrantySelection();
        let current_id = formValues['warranty_crew_id']
        let currentValue = items.find(item => item.value === current_id) || null;

        return <CxAutocomplete key="warranty_selection"
                               label="Garantie Verursacher *"
                               currentValue={currentValue}
                               items={items}
                               readOnly={readOnly}
                               onChange={handleWarrantyChange}
                               error={formErrors['warranty_crew_id']} />
    }

    const formFavoritesAction = (readOnly) => {
        return <CxPopover
            key="fav"
            label="Favoriten"
            iconName="favorite"
            data={favorites}
            loading={favoritesLoading}
            columns={[
                { field: "start_date", width: "100px", label: "Startdatum" },
                { field: "sub_task",   width: "300px", label: "Arbeitsvorgang" },
                { field: "team_lead",  width: "240px", label: "Gruppe/MA/Subunternehmer" }
            ]}
            render={{ start_date: (value) => DateUtil.convertIsoStringToString(value) }}
            onOpen={handleFavoriteLoad}
            onSelect={handleFavoriteSelect}
            readOnly={readOnly}
        />
    }

    const formExtension = (ext) => {
        let items = crew.getTeamLeadWorkerSelection();
        let currentValues = crew.getTeamMemberItems(ext.crew_ids);

        return (
            <CxGrid key={ext.id} size="xl" container spacing={2} direction="row" sx={{ml:4}}>
                <CxGrid size="xs">
                    <CxDatePickerNative id={ext.id} field="date" label="Einsatztag" disablePast={false} readOnly={readOnly}
                        handleChange={handleExtensionChange}
                        value={ext.date} error={ext.date_error} />
                </CxGrid>
                <CxGrid size="xlmd">
                    <CxAutocomplete key={"ac" + ext.id}
                                    label="Gruppe Einsatztag (inkl. Gruppenführer)"
                                    currentValue={currentValues}
                                    items={items}
                                    onChange={ (event, item) => handleExtensionChange(ext.id, "crew_ids", item)}
                                    error={ext.crew_ids_error}
                                    readOnly={readOnly}
                                    multiple={true} />
                </CxGrid>
                <CxGrid size="xs" align="center">
                    <CxIconButtonSecondary
                        label="Einsatztag"
                        iconName="delete"
                        disabled={readOnly}
                        onClick={() => handleExtensionRemove(ext.id)}
                    />
                </CxGrid>
            </CxGrid>
        );
    };

    const formExtensionList = () => {
        const extensions = formValues.extensions || [];
        return (
            <>
                {extensions.map( (ext, index) => (
                    formExtension(ext, index)
                ))}
            </>
        );
    };

    const formExtensionAction =(readOnly) => {
        const count = formValues.extensions?.length || 0;
        const isLimitReached = count >= MAX_EXTENSIONS;
        const label = isLimitReached ? "Max. Einsatztage erreicht" : "Einsatztag"
        const isDisabled = readOnly || isLimitReached;

        return <CxIconButtonSecondary
            key="ext"
            label={label}
            iconName="planExtend"
            disabled={isDisabled}
            onClick={handleExtensionAdd}
        />
    };

    const formCreateButtons = () => {
        return <CxCancelSaveButton handleSubmit={handleSubmit} />
    }

    const formUpdateButtons = () => {
        return <CxDeleteCancelSaveButton  handleSubmit={handleSubmit} />
    }

    const formCloseButton = () => {
        return <CxCloseButton handleSubmit={handleSubmit} />
    }

    const actionItems = [
        formFavoritesAction(readOnly),
        formExtensionAction(readOnly)
    ]

    //
    // return form content
    //
    return (
        <CxLocalization>
            <PageHeaderItems title={title} actionItems={actionItems} />
            <CxFormFeedback call={call} />

            <CxGrid size="lg">{ formCombox("task_code_key", "Arbeitskategorie *", code.getTaskSelection(), readOnly) }</CxGrid>
            <CxGrid size="lg">{ formSubTaskSelection(readOnly) }</CxGrid>

            { isWarranty() && (
                <>
                    <CxGrid size="lg">{ formWarrantySelection(readOnly) }</CxGrid>
                    <CxGrid size="lg">{ formTextField("warranty_cause", "Garantie Ursache/Bemerkung", 255, readOnly) }</CxGrid>
                </>
            )}

            <CxGrid size="sm">{ formDatePicker("start_date", "Startdatum *", readOnly)}</CxGrid>
            <CxGrid size="sm">{ formDatePicker("end_date", "Enddatum *", readOnly)}</CxGrid>
            <CxGrid size="sm">{ formTextField("dimension", "Mass", 255, readOnly) }</CxGrid>
            <CxGrid size="sm">{ formTextField("info", "Information", 2048, readOnly) }</CxGrid>

            <CxGrid size="lg">{ formTeamLeadSource(readOnly) }</CxGrid>
            <CxGrid size="sm">{ formTeamLeadSelection(readOnly) }</CxGrid>
            <CxGrid size="sm">{ formTextField("number", "Einsatz Nr.", 2, readOnly) }</CxGrid>
            <CxGrid size="xl">{ formTeamMemberSelection(readOnly) }</CxGrid>
            { formValues.extensions?.length > 0 && formExtensionList() }

            { isPump() && (
                <>
                    <CxGrid size="sm">{ formCombox("pump_code_key", "Pumpe", code.getPumpSelection(), readOnly) }</CxGrid>
                    <CxGrid size="sm">{ formCheckBox("cleared", "Abgeräumt", readOnly) }</CxGrid>
                    <CxGrid size="lg" />
                </>
            )}

            { !isPump() && (
                <>
                    <CxGrid size="sm">{ formCheckBox("cleared", "Abgeräumt", readOnly) }</CxGrid>
                    <CxGrid size="xlsm" />
                </>
            )}

            <CxMetaInfo values={formValues} />

            <CxGrid size="xl" justifyContent="flex-end">
                { readOnly && formCloseButton()}
                {!readOnly && !isUpdate && formCreateButtons()}
                {!readOnly &&  isUpdate && formUpdateButtons()}
            </CxGrid>

            <CxConfirmDialog
                open={openConfirm}
                title="Arbeitsvorgang löschen"
                text="Soll der Arbeitsvorgang gelöscht werden?"
                handleConfirm={handleConfirm}
                severity="Delete" />

            <CxSnack
                message={snack}
                handleClose={handleCloseSnack} />

        </CxLocalization>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// Create, Update and Delete
// --------------------------------------------------------------------------------------------------------------------


export function TaskCreate() {
    const { currentData } = useAppCtx();                // projectData
    const projectId = currentData.id;
    const title = "Arbeitsvorgang erstellen";

    let currentFormValues = Task.getFormValues(projectId)

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { RenderTask(title, currentFormValues, false) }
            </CxGrid>
        </Box>
    );
}

export function TaskClone() {
    const { currentData } = useAppCtx();                // taskData
    const title = "Arbeitsvorgang kopieren";

    const nextDay = DateUtil.getNextDayDateAsIsoString();

    let currentFormValues = Task.getFormValues(
        currentData.project_id,
        currentData.task_code_key,
        currentData.sub_task_code_key,
        nextDay,
        nextDay
    )

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { RenderTask(title, currentFormValues, false) }
            </CxGrid>
        </Box>
    );
}

export function TaskUpdate() {
    const { currentData } = useAppCtx();                // taskData
    const title = "Arbeitsvorgang bearbeiten";

    let currentFormValues = Task.mapDbToForm(currentData)

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { RenderTask(title, currentFormValues, true) }
            </CxGrid>
        </Box>
    );
}

export function TaskShow() {
    const { currentData } = useAppCtx();               // taskData (currentData)

    const title = "Arbeitsvorgang";
    const canManage = canManageDoc(currentData?.foreman_crew_id, currentData?.deputy_foreman_crew_id);
    const canCollab = canCollabDoc(currentData?.foreman_crew_id, currentData?.deputy_foreman_crew_id,
        currentData.team_lead_crew_id, currentData.team_member_crew_ids);

    let currentFormValues = Task.mapDbToForm(currentData);

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { RenderTask(title, currentFormValues, false, true) }
            </CxGrid>

            <Divider sx={{ my: 4 }} />

            <CxGrid container spacing={2}>
                <DocPanel
                    projectId={currentData.project_id}
                    taskId={currentData.id}
                    docCount={currentData.doc_count}
                    canManage={canManage}
                    canCollaborate={canCollab}
                />
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.
